/**
 * Featured Carousel — Dawn-compatible
 * Handles: randomize, drag-to-scroll, arrow nav, dot indicators, accessibility
 */

(function () {
  'use strict';

  class FeaturedCarousel {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('[data-carousel-track]');
      this.slides = Array.from(el.querySelectorAll('[data-carousel-slide]'));
      this.prevBtn = el.querySelector('[data-carousel-prev]');
      this.nextBtn = el.querySelector('[data-carousel-next]');
      this.dotsContainer = el.querySelector('[data-carousel-dots]');

      if (!this.track || !this.slides.length) return;

      this.settings = this._readSettings();
      this.currentIndex = 0;
      this.pageCount = 1;

      if (this.settings.randomize) this._shuffle();
      this._injectCustomProperties();
      this._injectSpacers(); // edge spacers before paint

      // Hide track until fully initialised to prevent large-image flash
      const outer = this.el.querySelector('[data-carousel-outer]');
      if (outer) outer.setAttribute('data-loading', '');

      // Wait one frame so card widths are painted before we measure
      requestAnimationFrame(() => {
        // Reset scroll to start — shuffle may leave the track mid-scroll
        this.track.scrollLeft = 0;
        this.currentIndex = 0;
        this._buildDots();
        this._bindArrows();
        this._bindDrag();
        this._bindScroll();
        this._updateState();

        // Reveal carousel now that everything is positioned
        if (outer) outer.removeAttribute('data-loading');

        // Rebuild dots if viewport resizes (slides-per-page may change)
        const ro = new ResizeObserver(() => {
          this._rebuildDots();
          this._updateState();
        });
        ro.observe(this.track);
      });
    }

    /* ------------------------------------------------------------------
       Settings
    ------------------------------------------------------------------ */
    _readSettings() {
      return {
        randomize: this.el.dataset.randomize !== 'false',
        showDots:  this.el.dataset.showDots  !== 'false',
      };
    }

    _injectCustomProperties() {
      // Card widths are now auto-calculated via CSS --carousel-cols.
    }

    _injectSpacers() {
      // Remove any existing spacers
      this.track.querySelectorAll('.carousel-spacer').forEach(s => s.remove());
      const style = 'flex: 0 0 var(--carousel-edge-padding); list-style: none; pointer-events: none;';
      const start = document.createElement('li');
      start.className = 'carousel-spacer';
      start.setAttribute('aria-hidden', 'true');
      start.style.cssText = style;
      const end = start.cloneNode();
      this.track.prepend(start);
      this.track.append(end);
    }

    /* ------------------------------------------------------------------
       Fisher-Yates shuffle
    ------------------------------------------------------------------ */
    _shuffle() {
      for (let i = this.slides.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const nodeA = this.slides[i];
        const nodeB = this.slides[j];
        const siblingA = nodeA.nextSibling === nodeB ? nodeA : nodeA.nextSibling;
        this.track.insertBefore(nodeA, nodeB);
        this.track.insertBefore(nodeB, siblingA);
        [this.slides[i], this.slides[j]] = [this.slides[j], this.slides[i]];
      }
    }

    /* ------------------------------------------------------------------
       Page calculations
    ------------------------------------------------------------------ */
    _slidesPerPage() {
      if (!this.slides.length) return 1;
      const trackW = this.track.clientWidth;
      const slideW = this.slides[0].offsetWidth;
      return Math.max(1, Math.round(trackW / slideW));
    }

    _calcPageCount() {
      return Math.max(1, Math.ceil(this.slides.length / this._slidesPerPage()));
    }

    _slideIndexToPage(slideIndex) {
      return Math.floor(slideIndex / this._slidesPerPage());
    }

    _pageToSlideIndex(page) {
      return Math.min(page * this._slidesPerPage(), this.slides.length - 1);
    }

    /* ------------------------------------------------------------------
       Dots — one per page
    ------------------------------------------------------------------ */
    _buildDots() {
      if (!this.dotsContainer) return;
      if (!this.settings.showDots) { this.dotsContainer.hidden = true; return; }

      this.pageCount = this._calcPageCount();
      this.dotsContainer.innerHTML = '';
      this.dots = [];

      if (this.pageCount <= 1) {
        this.dotsContainer.hidden = true;
        return;
      }

      this.dotsContainer.hidden = false;

      for (let p = 0; p < this.pageCount; p++) {
        const btn = document.createElement('button');
        btn.className = 'featured-carousel__dot';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', `Go to page ${p + 1} of ${this.pageCount}`);
        btn.addEventListener('click', () => this._scrollToIndex(this._pageToSlideIndex(p)));
        this.dotsContainer.appendChild(btn);
        this.dots.push(btn);
      }
    }

    _rebuildDots() {
      const newCount = this._calcPageCount();
      if (newCount !== this.pageCount) {
        this.pageCount = newCount;
        this._buildDots();
      }
    }

    /* ------------------------------------------------------------------
       Arrow buttons + keyboard
    ------------------------------------------------------------------ */
    _bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          const prevPage = Math.max(0, this._slideIndexToPage(this.currentIndex) - 1);
          this._scrollToIndex(this._pageToSlideIndex(prevPage));
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          const nextPage = Math.min(this.pageCount - 1, this._slideIndexToPage(this.currentIndex) + 1);
          this._scrollToIndex(this._pageToSlideIndex(nextPage));
        });
      }

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this._scrollToIndex(Math.max(0, this.currentIndex - 1));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this._scrollToIndex(Math.min(this.slides.length - 1, this.currentIndex + 1));
        }
      });
    }

    /* ------------------------------------------------------------------
       Drag-to-scroll
       Critical: scroll-snap-type is disabled during the drag so the lerp
       can move the track freely. It's re-enabled after release so the
       final snap-to-nearest feels crisp and intentional.
    ------------------------------------------------------------------ */
    _bindDrag() {
      const track = this.track;

      let isMouseDown      = false;
      let hasDragged       = false;
      let startX           = 0;
      let startScrollLeft  = 0;
      let targetScrollLeft = 0;
      let currentScrollLeft = 0;
      let rafId            = null;

      // ── Lerp loop ────────────────────────────────────────────────────
      const LERP = 0.16;

      const lerp = () => {
        const diff = targetScrollLeft - currentScrollLeft;
        if (Math.abs(diff) < 0.4) {
          track.scrollLeft = Math.round(targetScrollLeft);
          currentScrollLeft = targetScrollLeft;
          rafId = null;
          return;
        }
        currentScrollLeft += diff * LERP;
        track.scrollLeft = currentScrollLeft;
        rafId = requestAnimationFrame(lerp);
      };

      const startLerp = () => {
        if (rafId) cancelAnimationFrame(rafId);
        currentScrollLeft = track.scrollLeft;
        rafId = requestAnimationFrame(lerp);
      };

      // ── Snap toggle ───────────────────────────────────────────────────
      const disableSnap = () => { track.style.scrollSnapType = 'none'; };
      const enableSnap  = () => { track.style.scrollSnapType = '';     };

      // ── Mouse down ───────────────────────────────────────────────────
      const onMouseDown = (e) => {
        if (e.button !== 0) return;
        isMouseDown       = true;
        hasDragged        = false;
        startX            = e.clientX;
        startScrollLeft   = track.scrollLeft;
        targetScrollLeft  = track.scrollLeft;
        currentScrollLeft = track.scrollLeft;
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        e.preventDefault();
      };

      // ── Mouse move ───────────────────────────────────────────────────
      const onMouseMove = (e) => {
        if (!isMouseDown) return;
        const delta = e.clientX - startX;

        if (!hasDragged && Math.abs(delta) > 4) {
          hasDragged = true;
          disableSnap();
          track.classList.add('is-dragging');
          track.style.cursor = 'grabbing';
        }

        if (!hasDragged) return;

        targetScrollLeft = startScrollLeft - delta;
        targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, track.scrollWidth - track.clientWidth));

        if (!rafId) startLerp();
      };

      // ── Mouse up ─────────────────────────────────────────────────────
      const onMouseUp = () => {
        if (!isMouseDown) return;
        isMouseDown = false;
        track.classList.remove('is-dragging');
        track.style.cursor = '';

        if (!hasDragged) return;
        hasDragged = false;

        // Poll until lerp finishes, then re-enable snap and glide to nearest
        const afterLerp = () => {
          if (rafId) { requestAnimationFrame(afterLerp); return; }
          enableSnap();
          snapToNearest();
        };
        requestAnimationFrame(afterLerp);
      };

      // ── Snap to nearest slide ────────────────────────────────────────
      const snapToNearest = () => {
        const trackLeft = track.getBoundingClientRect().left;
        let closest = 0, closestDist = Infinity;
        this.slides.forEach((slide, i) => {
          const dist = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          if (dist < closestDist) { closestDist = dist; closest = i; }
        });
        this._scrollToIndex(closest);
      };

      // ── Touch: let browser handle natively, just cancel our lerp ─────
      const onTouchStart = () => {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      };

      // ── Bind ─────────────────────────────────────────────────────────
      track.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      track.addEventListener('touchstart', onTouchStart, { passive: true });
    }

    /* ------------------------------------------------------------------
       IntersectionObserver — keep currentIndex in sync while scrolling
    ------------------------------------------------------------------ */
    _bindScroll() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              const idx = this.slides.indexOf(entry.target);
              if (idx !== -1 && idx !== this.currentIndex) {
                this.currentIndex = idx;
                this._updateState();
              }
            }
          });
        },
        { root: this.track, threshold: 0.5 }
      );
      this.slides.forEach((s) => observer.observe(s));
    }

    /* ------------------------------------------------------------------
       Scroll to a slide index — used by arrows, dots, keyboard, post-drag
    ------------------------------------------------------------------ */
    _scrollToIndex(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.currentIndex = index;
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      this._updateState();
    }

    /* ------------------------------------------------------------------
       Sync arrows, dots, and aria to current state
    ------------------------------------------------------------------ */
    _updateState() {
      const currentPage = this._slideIndexToPage(this.currentIndex);
      const lastPage    = this.pageCount - 1;

      // Arrows disabled at boundaries
      if (this.prevBtn) this.prevBtn.disabled = currentPage <= 0;
      if (this.nextBtn) this.nextBtn.disabled = currentPage >= lastPage;

      // Dots
      if (this.dots && this.dots.length) {
        this.dots.forEach((dot, p) => {
          const active = p === currentPage;
          dot.setAttribute('aria-selected', String(active));
          dot.setAttribute('tabindex', active ? '0' : '-1');
        });
      }

      // Slide aria-hidden — mark off-screen slides hidden
      const spp = this._slidesPerPage();
      this.slides.forEach((slide, idx) => {
        const visible = idx >= this.currentIndex && idx < this.currentIndex + spp;
        slide.setAttribute('aria-hidden', String(!visible));
      });
    }
  }

  /* ------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------ */
  function init() {
    document.querySelectorAll('[data-section-id]').forEach((section) => {
      if (section.querySelector('[data-carousel-track]') && !section._carouselInstance) {
        section._carouselInstance = new FeaturedCarousel(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const s = e.target;
    if (s.querySelector('[data-carousel-track]')) s._carouselInstance = new FeaturedCarousel(s);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    delete e.target._carouselInstance;
  });
})();
